import { inject, Injectable, InjectionToken } from '@angular/core';
import { combineLatest, from, Observable, of } from 'rxjs';
import { map, skip, switchMap, take, tap } from 'rxjs/operators';
import { type ICountWorkspacesQuery, type IListWorkspacesQuery } from '@telespot/management/use-cases';
import { PaginatedStateEffects } from '@shared/ui';
import {
  WorkspaceData,
  WorkspacesState,
  WorkspacesStateModel,
} from './workspaces.state';

export const COUNT_WORKSPACES_QUERY = new InjectionToken<ICountWorkspacesQuery>('COUNT_WORKSPACES_QUERY');
export const LIST_WORKSPACES_QUERY = new InjectionToken<IListWorkspacesQuery>('LIST_WORKSPACES_QUERY');

@Injectable()
export class WorkspacesEffects extends PaginatedStateEffects<WorkspaceData, WorkspacesStateModel> {
  private readonly countWorkspacesQuery = inject<ICountWorkspacesQuery>(COUNT_WORKSPACES_QUERY);
  private readonly listWorkspacesQuery = inject<IListWorkspacesQuery>(LIST_WORKSPACES_QUERY);

  constructor(
    protected override readonly paginatedState: WorkspacesState,
  ) {
    super(paginatedState);

    this.reinitializeOnOrganizationChange(this.paginatedState.organizationId$.pipe(skip(1)));
    this.reinitializeOnCaseTypeFilterChange(this.paginatedState.caseTypeFilter$.pipe(skip(1)));
  }

  // ============================================================================
  // Selectors — re-exposed from WorkspacesState
  // ============================================================================

  public readonly organizationId$ = this.paginatedState.organizationId$;
  public readonly caseTypeFilter$ = this.paginatedState.caseTypeFilter$;
  public readonly workspaces$ = this.paginatedState.workspaces$;
  public readonly totalWorkspaces$ = this.paginatedState.totalWorkspaces$;

  // ============================================================================
  // Updater aliases
  // ============================================================================

  public readonly setOrganizationId = this.paginatedState.setOrganizationId;
  public readonly setCaseTypeFilter = this.paginatedState.setCaseTypeFilter;
  public readonly setSearchTerm = this.paginatedState.setSearchTerm;
  public readonly changePageSize = this.paginatedState.changePageSize;
  public readonly removeWorkspace = this.paginatedState.removeItem;

  // Backward compatibility aliases — preserve existing consumer call sites
  public readonly countWorkspaces = this.countItems;
  public readonly loadMoreWorkspaces = this.loadMoreItems;
  public readonly loadAllWorkspaces = this.loadAllItems;

  // ============================================================================
  // Custom effects
  // ============================================================================

  private readonly reinitializeOnOrganizationChange = this.effect((trigger$: Observable<string | undefined>) =>
    trigger$.pipe(tap(() => this.initializeItems()))
  );

  private readonly reinitializeOnCaseTypeFilterChange = this.effect((trigger$: Observable<string | undefined>) =>
    trigger$.pipe(tap(() => this.initializeItems()))
  );

  // ============================================================================
  // Abstract method implementations
  // ============================================================================

  protected override async countRepository(searchTerm?: string): Promise<number> {
    const snapshot = await combineLatest([this.paginatedState.organizationId$, this.paginatedState.caseTypeFilter$])
      .pipe(take(1))
      .toPromise();
    if (!snapshot) return 0;
    const [organizationId, caseTypeFilter] = snapshot;
    if (!organizationId) return 0;
    const count = await this.countWorkspacesQuery.execute(organizationId, caseTypeFilter, searchTerm);
    return count ?? 0;
  }

  protected override listFlow(
    limit: number,
    lastItem: WorkspaceData | undefined,
    searchTerm: string | undefined
  ): Observable<WorkspaceData[]> {
    return combineLatest([this.paginatedState.organizationId$, this.paginatedState.caseTypeFilter$]).pipe(
      take(1),
      switchMap(([organizationId, caseTypeFilter]) => {
        if (!organizationId) return of([] as WorkspaceData[]);
        return from(
          this.listWorkspacesQuery.execute({
            organizationId,
            limit,
            caseTypeId: caseTypeFilter,
            searchTerm,
            lastItemCreatedAt: lastItem?.createdAt,
          })
        ).pipe(
          map((items) =>
            items.map<WorkspaceData>((item) => ({
              id: item.id,
              name: item.name,
              organizationId: item.organizationId,
              caseTypeId: item.caseTypeId,
              createdAt: item.createdAt,
              usedStorage: item.usedStorage,
              numMembers: item.numMembers,
              numCases: item.numCases,
            }))
          )
        );
      })
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected override async deleteRepositoryItem(_itemId: string): Promise<boolean> {
    return false;
  }

  /**
   * Member and case counts come from {@link IListWorkspacesQuery} (denormalized on Workspace).
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected override processDeferredAttributes(_itemId: string): Observable<Partial<WorkspaceData>> {
    return of();
  }
}
